import { ReactElement, ReactNode, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { PlaidItemsContext } from "../firebaseio/PlaidItemsContext";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { BudgetsContext } from "../firebaseio/BudgetsContext";
import { LoadingInitialDataContext } from "../LoadUser";
import { RyeScreenWrapper } from "../rye/RyeScreenWrapper";
import { RyeMainContentPanel } from "../rye/RyeMainContentPanel";
import { RyeButton } from "../rye/RyeButton";
import { useRyeModal } from "../rye/RyeModal";
import { RyeCard } from "../rye/RyeCard";
import { RyeIcon } from "../rye/RyeIcon";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { buildClasses } from "../utils/buildClasses";
import { useMinuteTick } from "../utils/customHooks";
import {
  countLabel,
  getCategorySummary,
  getHomeOverview,
} from "./homeOverview";
import { MAIN_NAV_DEFINITIONS } from "../main_nav/mainNavDefinitions";
import { DollarAmount } from "../common/DollarAmount";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import { computeNetWorth } from "../derived/accountBalances";
import {
  SettingsTab,
  useOpenSettings,
} from "../settings_screen/settingsDefinitions";
import { AddNewAccountModal } from "../accounts_screen/AddNewAccountModal";

export function HomeScreen() {
  const accounts = useContext(AccountsContext);
  const plaidItems = useContext(PlaidItemsContext);
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  const budgets = useContext(BudgetsContext);
  const loading = useContext(LoadingInitialDataContext);
  useMinuteTick();
  const overview = getHomeOverview(accounts, plaidItems, transactions, budgets);
  const { convertLatest, isLoadingLatest } = useExchangeRates();
  const netWorth = computeNetWorth(accounts, convertLatest);
  const {
    accountCount,
    attentionCount,
    transactionCount,
    unreviewedCount,
    reviewedCount,
    activeBudgets,
    endedBudget,
    upcomingBudget,
  } = overview;
  // Each card waits only for the data it describes.
  const accountsLoading = loading.accounts || loading.plaid_items;
  const categoriesLoading = loading.categories || loading.category_groups;
  const transactionsLoading =
    loading.transactions || loading.accounts || loading.categories;
  const balancesLoading =
    loading.accounts || loading.preferences || isLoadingLatest;
  const budgetsLoading = loading.budgets || loading.transactions;
  const isLoading =
    accountsLoading ||
    categoriesLoading ||
    transactionsLoading ||
    balancesLoading ||
    budgetsLoading;
  const hasCategories = categories.size > 0;
  const { categoryCount, groupCount } = getCategorySummary(
    categories,
    categoryGroups,
  );

  // A brand-new user: nothing linked or organized yet.
  // Until there's both an account and a category: the first steps of setup.
  const showWelcome =
    !accountsLoading &&
    !categoriesLoading &&
    (accountCount === 0 || categoryCount === 0);

  const cards = (
    <>
      {showWelcome && (
        <HomeCard title="Welcome to Bread" icon="waving_hand">
          <div className={buildClasses("flex", "flex-col", "gap-3")}>
            <p>
              This home screen is designed to help you get set up and point you
              toward actions you may want to take.
            </p>
            <p>
              Follow the guidance in the cards below to link your financial
              institutions and begin syncing your account balances and
              transactions. Next, create some categories to organize and track
              how much you earn, spend, and save. When you&apos;re ready, plan
              ahead by creating a budget.
            </p>
            <p
              className={buildClasses(
                "flex",
                "items-center",
                "gap-1.5",
                "text-xs",
                "text-on-surface-400",
              )}
            >
              <RyeIcon name="info" size="xs" className="shrink-0" />
              <span className={buildClasses("text-xs")}>
                Heads up: this card will automatically disappear once you've
                linked an account and created a category.
              </span>
            </p>
          </div>
        </HomeCard>
      )}
      {/* Accounts and Categories are setup cards, so they share a row. */}
      <div
        className={buildClasses(
          "grid",
          "grid-cols-1",
          "md:grid-cols-2",
          "gap-4",
        )}
      >
        <HomeCard
          title="Accounts"
          icon="account_balance"
          paired
          loading={accountsLoading}
          warning={attentionCount > 0}
          headline={
            attentionCount > 0 ? (
              `${countLabel(attentionCount, "account")} ${attentionCount === 1 ? "needs" : "need"} your attention`
            ) : accountCount === 0 ? (
              "You don't have any financial accounts yet."
            ) : (
              <span className={buildClasses("flex", "items-center", "gap-2")}>
                <span
                  aria-hidden="true"
                  className={buildClasses(
                    "inline-flex",
                    "shrink-0",
                    "text-green",
                  )}
                >
                  <RyeIcon name="cloud_done" size="md" />
                </span>
                <span>All accounts are syncing properly.</span>
              </span>
            )
          }
          action={
            <HomeAction
              to={{
                settingsTab: "accounts",
                // A new user is here to link their first account, so go
                // straight to it.
                modal:
                  accountCount === 0 && attentionCount === 0 ? (
                    <AddNewAccountModal />
                  ) : undefined,
              }}
              text={
                attentionCount > 0
                  ? "Fix broken account syncing"
                  : accountCount === 0
                    ? "Link an account to get started"
                    : "View accounts"
              }
              primary={attentionCount > 0 || accountCount === 0}
              danger={attentionCount > 0}
            />
          }
        />

        <HomeCard
          title="Categories"
          icon="category"
          paired
          loading={categoriesLoading}
          headline={
            hasCategories
              ? `You have ${countLabel(categoryCount, "category", "categories")}${groupCount > 1 ? ` in ${countLabel(groupCount, "group")}` : ""}.`
              : "You don't have any categories yet."
          }
          action={
            <HomeAction
              to={{ settingsTab: "categories" }}
              text={
                hasCategories ? "View categories" : "Create your categories"
              }
              primary={!hasCategories}
            />
          }
        />
      </div>

      <HomeCard
        title="Transactions"
        icon="checklist_rtl"
        loading={transactionsLoading}
        headline={
          transactionCount === 0 ? (
            accountCount === 0 ? (
              "Link an account to start syncing transactions."
            ) : (
              "No transactions yet. They’ll sync automatically when available from your financial institution."
            )
          ) : unreviewedCount > 0 ? (
            `You have ${countLabel(unreviewedCount, "unreviewed transaction")}.${hasCategories ? "" : " Create some categories before categorizing them."}`
          ) : (
            <span className={buildClasses("flex", "items-center", "gap-2")}>
              <span
                aria-hidden="true"
                className={buildClasses(
                  "inline-flex",
                  "shrink-0",
                  "text-green",
                  "motion-safe:animate-fade-in",
                )}
              >
                <RyeIcon name="done_all" size="md" />
              </span>
              <span>All transactions have been reviewed.</span>
            </span>
          )
        }
        action={
          unreviewedCount > 0 && hasCategories ? (
            <HomeAction to="transactions" text="Review transactions" primary />
          ) : transactionCount > 0 && unreviewedCount === 0 ? (
            <HomeAction to="transactions" text="View transactions" />
          ) : undefined
        }
      />
      <HomeCard
        title={MAIN_NAV_DEFINITIONS.BALANCES.label}
        icon={MAIN_NAV_DEFINITIONS.BALANCES.icon}
        loading={balancesLoading}
        headline={
          accountCount > 0 ? (
            <>
              The net total of your accounts is{" "}
              <DollarAmount
                n={netWorth}
                showMinus
                showDollarSign
                align="span"
              />
              .
            </>
          ) : (
            "Link your accounts to see their balances."
          )
        }
        action={
          accountCount > 0 ? (
            <HomeAction to="balances" text="View balances" />
          ) : undefined
        }
      />
      <HomeCard
        title={MAIN_NAV_DEFINITIONS.CASH_FLOW.label}
        icon={MAIN_NAV_DEFINITIONS.CASH_FLOW.icon}
        loading={loading.transactions}
        headline={
          reviewedCount > 0
            ? "See where your money comes from and how it flows into spending and savings."
            : "Sync and categorize transactions to see how your money flows from income to expenses and savings."
        }
        action={
          reviewedCount > 0 ? (
            <HomeAction
              to="cash-flow"
              text="View cash flow"
              primary={unreviewedCount === 0}
            />
          ) : undefined
        }
      />
      <HomeCard
        title={MAIN_NAV_DEFINITIONS.BUDGETS.label}
        icon={MAIN_NAV_DEFINITIONS.BUDGETS.icon}
        loading={budgetsLoading}
        headline={
          <>
            {activeBudgets.length > 0 ? (
              activeBudgets.length === 1 ? (
                <>
                  Your budget{" "}
                  <em className={buildClasses("italic", "text-on-surface-800")}>
                    {activeBudgets[0].name}
                  </em>{" "}
                  is active.
                </>
              ) : (
                `You have ${countLabel(activeBudgets.length, "active budget")}.`
              )
            ) : (
              <>
                {budgets.size === 0
                  ? "Plan ahead with a budget."
                  : "No active budget."}{" "}
                {upcomingBudget ? (
                  <>
                    <em
                      className={buildClasses("italic", "text-on-surface-800")}
                    >
                      {upcomingBudget.name}
                    </em>{" "}
                    starts{" "}
                    {upcomingBudget.beginDateTimestampSecs.format(
                      "MMM D, YYYY",
                    )}
                    .
                  </>
                ) : endedBudget ? (
                  <>
                    <em
                      className={buildClasses("italic", "text-on-surface-800")}
                    >
                      {endedBudget.name}
                    </em>{" "}
                    ended{" "}
                    {endedBudget.endDateTimestampSecs.format("MMM D, YYYY")}.
                    You can duplicate it from Budgets to plan your next period.
                  </>
                ) : (
                  "Track your activity against a budget so you can spend with confidence and pursue your goals."
                )}
              </>
            )}
          </>
        }
        action={
          <HomeAction
            to="budgets"
            text={
              activeBudgets.length > 0
                ? "Track against your budget"
                : upcomingBudget
                  ? "View budgets"
                  : endedBudget
                    ? "Create next budget"
                    : "Create a budget"
            }
            primary={reviewedCount > 0 && unreviewedCount === 0}
          />
        }
      />
    </>
  );

  return (
    <RyeScreenWrapper>
      <RyeMainContentPanel title="Home" centerContent="narrow">
        {isLoading && (
          <p role="status" className={buildClasses("sr-only")}>
            Loading your overview…
          </p>
        )}
        <div className={buildClasses("flex", "flex-col", "gap-4")}>{cards}</div>
      </RyeMainContentPanel>
    </RyeScreenWrapper>
  );
}

function HomeRow({
  children,
  actions,
  warning = false,
  stacked = false,
}: {
  children: ReactNode;
  actions?: ReactNode;
  warning?: boolean;
  /** Puts the actions below the content, for cards that share a row. */
  stacked?: boolean;
}) {
  return (
    <div
      className={buildClasses(
        "flex",
        "justify-between",
        "gap-3",
        stacked
          ? buildClasses("flex-1", "flex-col", "items-start")
          : buildClasses("items-center", "sm:gap-5"),
        "py-3",
        warning
          ? buildClasses(
              "bg-red-50",
              "border",
              "border-red-200",
              "rounded-md",
              "px-3",
              "sm:px-4",
            )
          : buildClasses("first:pt-0", "last:pb-0"),
      )}
    >
      <div className={buildClasses("min-w-0", stacked ? "w-full" : "flex-1")}>
        {children}
      </div>
      {actions && (
        <div
          className={buildClasses(
            "flex",
            "items-end",
            stacked ? "max-w-full" : "max-w-[45%]",
            "shrink-0",
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

type HomeActionProps = {
  /**
   * A screen path, or a Settings tab to open over Home, optionally with a
   * dialog opened on top of it.
   */
  to: string | { settingsTab: SettingsTab; modal?: React.ReactNode };
  text: string;
  primary?: boolean;
  danger?: boolean;
};

type HomeCardProps = {
  title: string;
  icon: string;
  headline?: ReactNode;
  warning?: boolean;
  action?: ReactElement<HomeActionProps>;
  /** Shares a row with another card, with its action stacked. */
  paired?: boolean;
  /** Shows placeholders in place of the headline and action. */
  loading?: boolean;
  children?: ReactNode;
};

function HomeCard({
  title,
  icon,
  headline,
  warning = false,
  action,
  paired = false,
  loading = false,
  children,
}: HomeCardProps) {
  return (
    <RyeCard title={title} icon={icon} headerStyle="accent" fillHeight={paired}>
      <div
        className={buildClasses(
          { if: paired, then: buildClasses("flex-1", "flex", "flex-col") },
          "text-sm",
          "text-on-surface-500",
          "leading-relaxed",
        )}
      >
        {loading ? (
          <HomeRow
            stacked={paired}
            actions={
              <RyeSkeleton
                className={buildClasses("h-7", "w-40", "rounded-md")}
              />
            }
          >
            <RyeSkeleton shape="text" className={buildClasses("w-3/4")} />
          </HomeRow>
        ) : headline ? (
          <HomeRow warning={warning} actions={action} stacked={paired}>
            <h3
              className={buildClasses(
                "text-sm",
                warning ? "font-medium" : "font-normal",
                "break-words",
                warning ? "text-red-700" : "text-on-surface-800",
              )}
            >
              {headline}
            </h3>
          </HomeRow>
        ) : (
          children
        )}
      </div>
    </RyeCard>
  );
}

function HomeAction({
  to,
  text,
  primary = false,
  danger = false,
}: HomeActionProps) {
  const navigate = useNavigate();
  const openSettings = useOpenSettings();
  const { openModal } = useRyeModal();

  function openSettingsTab({
    settingsTab,
    modal,
  }: {
    settingsTab: SettingsTab;
    modal?: React.ReactNode;
  }) {
    openSettings(settingsTab);
    if (modal !== undefined) openModal(modal);
  }

  return (
    <div
      className={buildClasses(
        "min-w-0",
        "max-w-full",
        "[&_button]:max-w-full",
        "[&_button]:h-auto",
        "[&_button]:min-h-7",
        "[&_button]:py-1",
      )}
    >
      <RyeButton
        text={text}
        size="sm"
        variant={primary ? "filled" : "outlined"}
        vibe={danger ? "danger" : primary ? "primary" : "subdued"}
        onClick={() =>
          typeof to === "string"
            ? navigate(to.startsWith("/") ? to : `/${to}`)
            : openSettingsTab(to)
        }
      />
    </div>
  );
}
